/* MapGreetings — sezione "Saluti da mappa" nel Match hub.
   Mostra i saluti 👋 ricevuti tramite la funzione "Chi c'è online"
   (source='presence' in match_likes) con avatar + cane + distanza-in-tempo
   e permette di rispondere o segnare come letto.
*/
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hand, MapPin, X, Loader2, Check, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { hapticTap } from "@/lib/haptics";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const d2 = Math.floor(h / 24);
  return `${d2}g fa`;
}

export default function MapGreetings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/match/greetings");
      setItems(data.items || []);
      setUnseen(data.unseen || 0);
    } catch (e) {
      console.warn("greetings load", formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openSheet = async () => {
    hapticTap();
    setOpen(true);
    // Mark all unseen as read
    const toMark = items.filter((it) => !it.seen_by_recipient);
    if (toMark.length) {
      Promise.all(toMark.map((it) => api.post(`/match/greetings/${it.id}/read`).catch(() => {})))
        .then(() => setUnseen(0));
    }
  };

  const respond = async (g) => {
    setResponding(g.id);
    try {
      const { data } = await api.post(`/match/greetings/${g.id}/respond`);
      hapticTap();
      setItems((arr) => arr.map((it) => it.id === g.id ? { ...it, responded: true } : it));
      // Reciprocated wave = new match — open the chat straight away so
      // the user actually sees the conversation instead of a dead-end toast.
      // Fallback: if the backend didn't return a URL for any reason, at
      // least show a success toast so we don't silently swallow it.
      const chatUrl = data?.chat_url || "/app/messaggi";
      toast.success(
        `Match con ${g.sender?.name || "utente"}! 🎉  Apro la chat…`,
        { icon: "👋", duration: 2200 }
      );
      setOpen(false);
      setTimeout(() => navigate(chatUrl), 600);
    } catch (e) {
      toast.error(formatErr(e) || "Impossibile rispondere adesso");
    } finally {
      setResponding(null);
    }
  };

  if (items.length === 0 && !loading) return null;

  // Preview strip: overlapping avatars + count
  const preview = items.slice(0, 4);

  return (
    <>
      <button
        data-testid="match-greetings-btn"
        onClick={openSheet}
        className="w-full mt-4 rounded-2xl px-4 py-3 flex items-center gap-3 border transition text-left"
        style={{
          background: "linear-gradient(135deg, #FFF6E6 0%, #FFEAD1 100%)",
          borderColor: "#F0D9B0",
          boxShadow: "0 10px 24px -12px rgba(255,122,26,0.35)",
        }}
      >
        <div className="relative shrink-0 h-10 w-10">
          <div className="h-10 w-10 rounded-2xl flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                        boxShadow: "0 6px 14px -6px rgba(255,122,26,0.55)" }}>
            <Hand className="h-5 w-5 text-white" />
          </div>
          {unseen > 0 && (
            <span data-testid="match-greetings-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#E11D48] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
              {unseen > 9 ? "9+" : unseen}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-[#1A1A1A] leading-tight inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-[#FF7A1A]" /> Saluti dalla mappa
          </div>
          <div className="text-[11.5px] text-[#7A7A7A] leading-tight mt-0.5 truncate">
            {items.length} salut{items.length === 1 ? "o" : "i"} da chi ti ha visto online nei 25 km
          </div>
        </div>
        {/* Overlapping avatars */}
        <div className="flex -space-x-2 shrink-0">
          {preview.map((g) => (
            <img
              key={g.id}
              src={g.sender?.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(g.sender?.name || "?")}&background=FF7A1A&color=fff`}
              alt=""
              className="h-8 w-8 rounded-full border-2 border-white object-cover bg-[#F5EFE6]"
            />
          ))}
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <GreetingsSheet
            items={items}
            responding={responding}
            onClose={() => setOpen(false)}
            onRespond={respond}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function GreetingsSheet({ items, responding, onClose, onRespond }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-[3px] flex items-end justify-center"
      onClick={onClose}
      data-testid="match-greetings-sheet"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] text-[#1a1a1a] rounded-t-[28px] p-5 pb-8 max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" /> Presenza dal vivo
            </div>
            <div className="font-display text-xl mt-0.5">Saluti dalla mappa</div>
            <div className="text-[12px] text-[#7A7A7A] mt-0.5">
              👋 arrivati quando eri online nei 25 km · rispondi per aprire una chat
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center"
            aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>

        {items.length === 0 && (
          <div className="py-8 text-center text-[13px] text-[#7A7A7A]">
            Nessun saluto ricevuto.
          </div>
        )}

        <div className="space-y-2 mt-2">
          {items.map((g) => (
            <div key={g.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#F0EBE3]">
              <img
                src={g.sender?.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(g.sender?.name || "?")}&background=FF7A1A&color=fff`}
                alt=""
                className="h-12 w-12 rounded-full object-cover shrink-0 border border-[#F0EBE3]"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-[#1A1A1A] truncate">
                    {g.sender?.name || "Utente ORIO"}
                  </span>
                  <span className="text-[10.5px] text-[#9A9A9A] shrink-0">{timeAgo(g.created_at)}</span>
                </div>
                <div className="text-[12px] text-[#7A7A7A] leading-tight truncate">
                  {g.dog?.name ? (
                    <span className="inline-flex items-center gap-1"><PawPrint className="h-3 w-3" /> {g.dog.name}{g.dog.breed ? ` · ${g.dog.breed}` : ""}</span>
                  ) : (g.sender?.city || "Nei tuoi 25 km")}
                </div>
              </div>
              {g.responded ? (
                <div className="shrink-0 h-9 px-3 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5"
                     style={{ background: "#E8F4EA", color: "#2DBA60" }}>
                  <Check className="h-3.5 w-3.5" /> Risposto
                </div>
              ) : (
                <button
                  data-testid={`match-greetings-respond-${g.id}`}
                  disabled={responding === g.id}
                  onClick={() => onRespond(g)}
                  className="shrink-0 h-9 px-3 rounded-full text-[12px] font-semibold text-white inline-flex items-center gap-1.5"
                  style={{
                    background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                    boxShadow: "0 6px 16px -6px rgba(255,122,26,0.55)",
                    opacity: responding === g.id ? 0.6 : 1,
                  }}
                >
                  {responding === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hand className="h-3.5 w-3.5" />}
                  Rispondi
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl p-3 border-2 border-dashed" style={{ borderColor: "#F0EBE3" }}>
          <div className="text-[11px] text-[#7A7A7A] leading-relaxed">
            💡 I saluti arrivano quando qualcuno ti trova sulla mappa con la funzione <strong>Chi c&apos;è online</strong> e tocca 👋 sul tuo profilo.
            Se rispondi, si sblocca la chat in Match.
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
